import { Anchor, Box, Text } from 'grommet';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const UNDO_MS = 5000;

/** Deletes waiting out their undo toast, across the app, so a page close can send them first */
const pending = new Map<string, () => void>();

/**
 * Sends waiting deletes as the page goes away. Ordinary requests are cancelled when the page unloads,
 * so for as long as these take to start (the generated API client builds its headers asynchronously,
 * but within this task) every fetch is marked keepalive, which the browser completes after unload.
 */
const flushAll = () => {
  if (!pending.size) return;
  const fetch = window.fetch;
  window.fetch = (input, init) => fetch.call(window, input, { ...init, keepalive: true });
  pending.forEach((commit) => commit());
  setTimeout(() => {
    window.fetch = fetch;
  });
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushAll);
  window.addEventListener('pagehide', flushAll);
}

let nextId = 0;

export type UndoableDelete = {
  /** For the toast: `"{title}" deleted` */
  title: string;
  /** Hide the item now */
  hide: () => void;
  /** Bring it back: Undo was pressed */
  restore: () => void;
  /** Actually delete it once the toast has gone; return false to restore a failed delete */
  commit: () => Promise<unknown> | void;
};

/**
 * A line item's delete, with no confirmation: the item disappears at once and a toast offers Undo
 * for 5 seconds; the delete is sent when the toast goes. If the component that asked unmounts first
 * (the user moves to another page) its deletes are sent then, and the toast goes with them.
 */
export const useUndoableDelete = () => {
  const mine = useRef(new Set<string>());

  useEffect(
    () => () => {
      mine.current.forEach((id) => {
        pending.get(id)?.();
        toast.dismiss(id);
      });
    },
    []
  );

  return ({ title, hide, restore, commit }: UndoableDelete) => {
    const id = `undo-delete-${nextId++}`;
    let settled = false;
    const settle = () => {
      settled = true;
      pending.delete(id);
      mine.current.delete(id);
    };
    const send = async () => {
      if (settled) return;
      settle();
      try {
        if ((await commit()) === false) restore();
      } catch (error: unknown) {
        restore();
        toast.error(error instanceof Error ? error.message : 'Could not delete item.');
      }
    };
    const undo = () => {
      if (settled) return;
      settle();
      toast.dismiss(id);
      restore();
    };

    hide();
    pending.set(id, send);
    mine.current.add(id);
    toast(
      <Box direction="row" align="center" justify="between" gap="medium">
        <Text size="14px" color="white">
          &ldquo;{title}&rdquo; deleted
        </Text>
        <Anchor label="Undo" size="14px" weight={600} color="#9fc0ff" onClick={undo}
          style={{ whiteSpace: 'nowrap', flexShrink: 0 }} />
      </Box>,
      {
        toastId: id,
        autoClose: UNDO_MS,
        closeButton: false,
        icon: false,
        className: 'undo-toast',
        style: { background: '#222', color: '#fff', borderRadius: '8px' },
        onClose: send,
      }
    );
  };
};
